"use client";
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const LIME = '#ceff1a';
const PURPLE = '#3730a3';
const CARD_BG = '#ffffff';
const ACTIVE_BG = '#3730a3';
const REVEAL = '#ffffff';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduced(mq.matches);
        const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return reduced;
}

/* ───────────────────────── reveal illustrations ─────────────────────────
   Large, white abstract line art. Only ever visible against the
   purple hover/active surface, so colour is hard-coded to REVEAL.     */

function SearchVisual() {
    return (
        <svg width="184" height="184" viewBox="0 0 184 184" fill="none" aria-hidden="true">
            <rect x="14" y="92" width="128" height="36" rx="18" stroke={REVEAL} strokeWidth="2.5" opacity="0.7" />
            <rect x="32" y="105" width="62" height="4.5" rx="2.25" fill={REVEAL} opacity="0.45" />
            <rect x="32" y="114" width="40" height="4.5" rx="2.25" fill={REVEAL} opacity="0.28" />
            <g transform="rotate(6 128 54)">
                <circle cx="128" cy="54" r="28" stroke={REVEAL} strokeWidth="4.5" opacity="0.85" />
                <path d="M148 74l18 18" stroke={REVEAL} strokeWidth="4.5" strokeLinecap="round" opacity="0.85" />
            </g>
        </svg>
    );
}

function KeywordVisual() {
    const nodes = [
        { cx: 132, cy: 30, r: 9 },
        { cx: 168, cy: 74, r: 6 },
        { cx: 150, cy: 122, r: 7 },
        { cx: 98, cy: 140, r: 5 },
        { cx: 70, cy: 96, r: 11 },
    ];
    return (
        <svg width="184" height="184" viewBox="0 0 184 184" fill="none" aria-hidden="true">
            <g stroke={REVEAL} strokeWidth="1.6" opacity="0.4">
                <path d="M70 96L132 30" />
                <path d="M70 96L168 74" />
                <path d="M70 96L150 122" />
                <path d="M70 96L98 140" />
                <path d="M132 30L168 74" />
            </g>
            {nodes.map((n, i) => (
                <circle key={i} cx={n.cx} cy={n.cy} r={n.r} fill={REVEAL} opacity={i === 4 ? 0.85 : 0.55} />
            ))}
        </svg>
    );
}

function ClusterVisual() {
    const orbit = [
        { cx: 96, cy: 34 },
        { cx: 152, cy: 78 },
        { cx: 132, cy: 138 },
        { cx: 62, cy: 130 },
    ];
    return (
        <svg width="184" height="184" viewBox="0 0 184 184" fill="none" aria-hidden="true">
            <circle cx="96" cy="90" r="52" stroke={REVEAL} strokeWidth="1.4" opacity="0.22" strokeDasharray="3 6" />
            <g stroke={REVEAL} strokeWidth="1.6" opacity="0.4">
                {orbit.map((o, i) => (
                    <path key={i} d={`M96 90L${o.cx} ${o.cy}`} />
                ))}
            </g>
            {orbit.map((o, i) => (
                <circle key={i} cx={o.cx} cy={o.cy} r="8" stroke={REVEAL} strokeWidth="2.5" opacity="0.6" />
            ))}
            <circle cx="96" cy="90" r="20" fill={REVEAL} opacity="0.85" />
        </svg>
    );
}

function CommercialVisual() {
    return (
        <svg width="184" height="184" viewBox="0 0 184 184" fill="none" aria-hidden="true">
            <rect x="18" y="30" width="120" height="88" rx="12" stroke={REVEAL} strokeWidth="2.5" opacity="0.7" />
            <path d="M18 54h120" stroke={REVEAL} strokeWidth="2.5" opacity="0.5" />
            <circle cx="32" cy="42" r="3" fill={REVEAL} opacity="0.5" />
            <circle cx="44" cy="42" r="3" fill={REVEAL} opacity="0.5" />
            <rect x="34" y="70" width="66" height="6" rx="3" fill={REVEAL} opacity="0.4" />
            <rect x="34" y="84" width="44" height="6" rx="3" fill={REVEAL} opacity="0.25" />
            <g transform="rotate(-8 150 128)">
                <path d="M126 108h34l14 14-24 24-24-24 0-14z" stroke={REVEAL} strokeWidth="3" strokeLinejoin="round" opacity="0.85" />
                <circle cx="150" cy="122" r="3.5" fill={REVEAL} opacity="0.85" />
            </g>
        </svg>
    );
}

function InformationalVisual() {
    return (
        <svg width="184" height="184" viewBox="0 0 184 184" fill="none" aria-hidden="true">
            <g transform="rotate(-6 90 88)">
                <rect x="34" y="30" width="92" height="118" rx="10" stroke={REVEAL} strokeWidth="2" opacity="0.35" />
            </g>
            <g transform="rotate(4 90 90)">
                <rect x="42" y="24" width="92" height="118" rx="10" fill={ACTIVE_BG} stroke={REVEAL} strokeWidth="2.5" opacity="0.9" />
            </g>
            <rect x="54" y="46" width="60" height="6" rx="3" fill={REVEAL} opacity="0.75" />
            <rect x="54" y="62" width="68" height="4.5" rx="2.25" fill={REVEAL} opacity="0.4" />
            <rect x="54" y="74" width="68" height="4.5" rx="2.25" fill={REVEAL} opacity="0.4" />
            <rect x="54" y="86" width="46" height="4.5" rx="2.25" fill={REVEAL} opacity="0.4" />
            <circle cx="60" cy="112" r="4" fill={REVEAL} opacity="0.6" />
            <rect x="70" y="109" width="52" height="6" rx="3" fill={REVEAL} opacity="0.3" />
        </svg>
    );
}

function LinkingVisual() {
    return (
        <svg width="184" height="184" viewBox="0 0 184 184" fill="none" aria-hidden="true">
            <g transform="rotate(-5 60 66)">
                <rect x="20" y="34" width="76" height="56" rx="10" stroke={REVEAL} strokeWidth="2.5" opacity="0.7" />
                <rect x="30" y="46" width="40" height="4.5" rx="2.25" fill={REVEAL} opacity="0.5" />
                <rect x="30" y="56" width="28" height="4.5" rx="2.25" fill={REVEAL} opacity="0.3" />
            </g>
            <g transform="rotate(7 132 120)">
                <rect x="94" y="90" width="76" height="56" rx="10" stroke={REVEAL} strokeWidth="2.5" opacity="0.7" />
                <rect x="104" y="102" width="40" height="4.5" rx="2.25" fill={REVEAL} opacity="0.5" />
                <rect x="104" y="112" width="28" height="4.5" rx="2.25" fill={REVEAL} opacity="0.3" />
            </g>
            <path d="M74 76L112 100" stroke={REVEAL} strokeWidth="2.5" strokeDasharray="1 7" strokeLinecap="round" opacity="0.6" />
            <circle cx="74" cy="76" r="6" fill={REVEAL} opacity="0.85" />
            <circle cx="112" cy="100" r="6" fill={REVEAL} opacity="0.85" />
        </svg>
    );
}

interface FocusItem {
    title: string;
    icon: React.ReactNode;
    desc: string;
    Visual: React.ComponentType;
}

const items: FocusItem[] = [
    {
        title: 'Search Intent Mapping',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="14" cy="14" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We identify what people are actually trying to accomplish behind a search — learn, compare, solve a problem or make a purchase decision.',
        Visual: SearchVisual,
    },
    {
        title: 'Keyword & Topic Mapping',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="7" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="7" cy="25" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="25" cy="25" r="3" stroke="currentColor" strokeWidth="2" />
                <path d="M16 10v6M16 16l-9 6M16 16l9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We map important topics and search terms to the pages best suited to answer them, reducing unnecessary overlap between URLs.',
        Visual: KeywordVisual,
    },
    {
        title: 'Topic Clusters',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="5" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="27" cy="16" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="27" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="5" cy="16" r="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M16 12v-4M20 16h4M16 20v4M12 16H8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We build connected groups of content around important subjects to create greater depth and clearer relationships between pages.',
        Visual: ClusterVisual,
    },
    {
        title: 'Commercial Content',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="7" y="4" width="18" height="24" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M12 11h8M12 16h8M12 21h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We develop service pages, comparison pages, pricing resources, industry content and decision-stage content designed around genuine customer needs.',
        Visual: CommercialVisual,
    },
    {
        title: 'Informational Content',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="2" />
                <path d="M16 21v-6M16 11.5v-.1" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
        ),
        desc: "We answer the questions people ask before they're ready to buy — helping your brand become useful earlier in the customer journey.",
        Visual: InformationalVisual,
    },
    {
        title: 'Internal Linking',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="12" width="12" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
                <rect x="16" y="12" width="12" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
                <path d="M12 16h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'We connect related pages so users can move naturally from learning about a problem to exploring a solution, while helping search engines understand the relationship between your content.',
        Visual: LinkingVisual,
    },
];

function FocusCard({ item, index, reduced }: { item: FocusItem; index: number; reduced: boolean }) {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [tapped, setTapped] = useState(false);
    const active = hovered || focused || tapped;
    const num = String(index + 1).padStart(2, '0');
    const Visual = item.Visual;

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: reduced ? 0.01 : 0.5, delay: reduced ? 0 : (index % 3) * 0.08 }}
            role="group"
            aria-label={item.title}
            aria-expanded={active}
            tabIndex={0}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onClick={() => setTapped((t) => !t)}
            className="relative overflow-hidden rounded-[22px] p-8 md:p-9 flex flex-col justify-between cursor-pointer outline-none"
            style={{
                minHeight: 400,
                background: active ? ACTIVE_BG : CARD_BG,
                border: `1px solid ${active ? 'rgba(255,255,255,0.14)' : 'rgba(8,4,0,0.18)'}`,
                boxShadow: focused ? `0 0 0 2px ${LIME}` : 'none',
                transition: reduced
                    ? 'background-color 200ms ease, border-color 200ms ease, box-shadow 150ms ease'
                    : `background-color 550ms ${EASE}, border-color 400ms ease, box-shadow 150ms ease`,
            }}
        >
            {/* reveal illustration — clipped by the card's own overflow-hidden */}
            <div
                className="absolute pointer-events-none"
                style={{
                    right: -18,
                    bottom: -14,
                    opacity: active ? 1 : 0,
                    transform: reduced
                        ? 'none'
                        : active ? 'translateY(0) scale(1.05)' : 'translateY(30px) scale(0.96)',
                    transition: reduced
                        ? 'opacity 250ms ease'
                        : `opacity 600ms ${EASE}, transform 650ms ${EASE}`,
                }}
            >
                <Visual />
            </div>

            {/* top block: number / divider / icon / title / desc */}
            <div className="relative z-10 flex flex-col gap-5">
                <div className="flex flex-col gap-3">
                    <span
                        className="text-sm font-bold"
                        style={{
                            fontFamily: CLASH,
                            color: active ? 'rgba(255,255,255,0.45)' : INK,
                            transition: `color 400ms ${EASE}`,
                        }}
                    >
                        {num}
                    </span>
                    <div
                        className="h-px w-full"
                        style={{
                            background: active ? 'rgba(255,255,255,0.2)' : 'rgba(8,4,0,0.3)',
                            transition: 'background-color 400ms ease',
                        }}
                    />
                </div>

                <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                    style={{
                        background: active ? 'rgba(206,255,26,0.1)' : 'rgba(55,48,163,0.08)',
                        border: `1px solid ${active ? 'rgba(206,255,26,0.25)' : 'rgba(55,48,163,0.18)'}`,
                        color: active ? LIME : PURPLE,
                        transition: `background-color 400ms ${EASE}, border-color 400ms ease, color 400ms ${EASE}`,
                    }}
                >
                    {item.icon}
                </div>

                <div className="flex flex-col gap-2 max-w-[86%]">
                    <h3
                        className="text-lg md:text-xl font-bold leading-snug"
                        style={{
                            fontFamily: CLASH,
                            color: active ? '#ffffff' : INK,
                            letterSpacing: '0.02em',
                            transition: `color 400ms ${EASE}`,
                        }}
                    >
                        {item.title}
                    </h3>
                    <p
                        className="text-sm leading-relaxed"
                        style={{
                            fontFamily: MANROPE,
                            color: active ? 'rgba(255,255,255,0.78)' : 'rgba(8,4,0,0.6)',
                            letterSpacing: '0.01em',
                            transition: `color 400ms ${EASE}`,
                        }}
                    >
                        {item.desc}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}

export default function ContentStrategyWhatWeBuild() {
    const reduced = usePrefersReducedMotion();

    return (
        <section className="relative overflow-hidden py-16 md:py-24 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff' }}>
            <div className="max-w-[1400px] mx-auto flex flex-col gap-14">
                <div className="flex flex-col gap-4">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        Our Focus Areas
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        What We Build
                    </motion.h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((it, i) => (
                        <FocusCard key={it.title} item={it} index={i} reduced={reduced} />
                    ))}
                </div>
            </div>
        </section>
    );
}
